// schema.org JSON-LD builders for the React pages (rendered by SEOHead).
// backend/utils/seo.py builds the same objects for the crawler pages.
// Honest data only: ratings and reviews only when real ones exist, no
// priceValidUntil (prices have no fixed end date), no guessed fees.

import { RETURN_POLICY_LD, STORE_ID } from '../structuredData';
import { SITE_NAME, SITE_URL } from './meta';

const CONTEXT = 'https://schema.org';
export const ITEM_LIST_MAX = 30;

const absolute = (url) => (/^https?:\/\//.test(String(url || '')) ? url : `${SITE_URL}${url || '/'}`);
const text = (value) => (value && typeof value === 'object' ? value.en || value.ar || '' : String(value ?? '')).trim();

/** BreadcrumbList for [{ name, url }] (url absolute or site-relative). */
export const breadcrumbList = (items) => ({
  '@context': CONTEXT,
  '@type': 'BreadcrumbList',
  itemListElement: (items || []).filter((b) => b && text(b.name)).map((b, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: text(b.name),
    item: absolute(b.url),
  })),
});

/**
 * ItemList of product pages for a listing (category, brand, collection),
 * the first ITEM_LIST_MAX pieces in the order shown. null when empty.
 */
export const itemList = (products, { name } = {}) => {
  const rows = (products || []).filter((p) => p && p.id).slice(0, ITEM_LIST_MAX);
  if (!rows.length) return null;
  return {
    '@context': CONTEXT,
    '@type': 'ItemList',
    ...(name ? { name } : {}),
    numberOfItems: rows.length,
    itemListElement: rows.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${SITE_URL}/products/${encodeURIComponent(p.id)}`,
      name: text(p.name) || 'Luxury piece',
    })),
  };
};

/** FAQPage from [{ question, answer }] (the real FAQ content). null when empty. */
export const faqPage = (faqs) => {
  const rows = (faqs || []).filter((f) => f && text(f.question) && text(f.answer));
  if (!rows.length) return null;
  return {
    '@context': CONTEXT,
    '@type': 'FAQPage',
    mainEntity: rows.map((f) => ({
      '@type': 'Question',
      name: text(f.question),
      acceptedAnswer: { '@type': 'Answer', text: text(f.answer) },
    })),
  };
};

export const productId = (canonical) => `${canonical}#product`;

/**
 * schema.org Product for a product page. `product.price` is the price the
 * shopper sees, incl. UAE VAT while the store is VAT-registered (like the
 * Google feed and backend utils/seo; `vatIncluded: false` otherwise),
 * so the offer says so. aggregateRating only with real reviews (never a
 * made-up count).
 */
export const productJsonLd = (product, { canonicalUrl, image } = {}) => {
  const price = Number(product.price || 0).toFixed(2);
  const rating = Number(product.rating);
  const reviewCount = Number(product.reviewCount);
  const availability = product.availability
    || (product.inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock');
  return {
    '@context': CONTEXT,
    '@type': 'Product',
    ...(canonicalUrl ? { '@id': productId(canonicalUrl) } : {}),
    name: typeof product.name === 'object' ? product.name.en : product.name,
    image: product.images?.length ? product.images : [image],
    description: typeof product.description === 'object' ? product.description.en : product.description,
    ...(product.sku ? { sku: product.sku } : {}),
    ...(product.category ? { category: product.category } : {}),
    ...(product.colors?.length ? { color: product.colors.join(', ') } : {}),
    ...(canonicalUrl ? { url: canonicalUrl } : {}),
    brand: { '@type': 'Brand', name: product.brand || SITE_NAME },
    offers: {
      '@type': 'Offer',
      ...(canonicalUrl ? { url: canonicalUrl } : {}),
      priceCurrency: 'AED',
      price,
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price,
        priceCurrency: 'AED',
        // The shop's price includes VAT only while the store is VAT-registered.
        valueAddedTaxIncluded: product.vatIncluded !== false,
      },
      itemCondition: 'https://schema.org/NewCondition',
      availability,
      // The store's @id (index.html / StoreStructuredData) so the offer joins the entity.
      seller: { '@type': 'Organization', '@id': STORE_ID, name: SITE_NAME },
      // From the store facts (structuredData.returnPolicyLd); the window-only default until they load.
      hasMerchantReturnPolicy: product.returnPolicy || RETURN_POLICY_LD,
      // From the real delivery-zone table (utils/structuredData.offerShippingDetails).
      ...(product.shippingDetails ? { shippingDetails: product.shippingDetails } : {}),
    },
    ...(rating > 0 && reviewCount > 0 ? {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: Number(rating.toFixed(1)),
        reviewCount,
        bestRating: 5,
        worstRating: 1,
      },
    } : {}),
  };
};

export const REVIEW_LD_MAX = 5;

/**
 * Up to REVIEW_LD_MAX approved reviews as schema.org Review items on the same
 * Product (`@id`), so search engines join them with the page's Product.
 * null without real reviews (never invented).
 */
export const productReviews = (reviews, { canonicalUrl, name } = {}) => {
  const rows = (reviews || [])
    .filter((r) => r && Number(r.rating) >= 1 && Number(r.rating) <= 5)
    .slice(0, REVIEW_LD_MAX);
  if (!rows.length || !canonicalUrl) return null;
  return {
    '@context': CONTEXT,
    '@type': 'Product',
    '@id': productId(canonicalUrl),
    ...(name ? { name } : {}),
    review: rows.map((r) => {
      const body = text(r.comment || r.body || r.text);
      const date = String(r.created_at || '').slice(0, 10);
      return {
        '@type': 'Review',
        reviewRating: { '@type': 'Rating', ratingValue: Number(r.rating), bestRating: 5, worstRating: 1 },
        author: { '@type': 'Person', name: text(r.user_name || r.author_name || r.name) || 'Verified client' },
        ...(text(r.title) ? { name: text(r.title) } : {}),
        ...(body ? { reviewBody: body } : {}),
        ...(/^\d{4}-\d{2}-\d{2}$/.test(date) ? { datePublished: date } : {}),
      };
    }),
  };
};

/**
 * schema.org Article for a buying guide (GET /content/guides/{slug}), the same
 * shape as backend utils/geo_pages.article_ld: headline, dates, language, the
 * store as author and publisher (@id), the page as mainEntityOfPage.
 */
export const articleJsonLd = (guide, { canonicalUrl, lang = 'en' } = {}) => {
  if (!guide || !guide.slug || !canonicalUrl) return null;
  const pick = (value) => (value && typeof value === 'object' ? (value[lang] || value.en || '') : String(value || '')).trim();
  const store = { '@type': 'Organization', '@id': STORE_ID, name: SITE_NAME };
  return {
    '@context': CONTEXT,
    '@type': 'Article',
    '@id': `${canonicalUrl}#article`,
    headline: pick(guide.title).slice(0, 110),
    description: pick(guide.description),
    inLanguage: lang === 'ar' ? 'ar-AE' : 'en-AE',
    datePublished: guide.published,
    dateModified: guide.updated_at || guide.published,
    image: [`${SITE_URL}/og-image.jpg`],
    url: canonicalUrl,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    author: store,
    publisher: { ...store, logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` } },
    isPartOf: { '@id': `${SITE_URL}/#website` },
  };
};

/** Serialised for a <script type="application/ld+json">: `<` escaped so text cannot close it. */
export const serializeJsonLd = (data) => JSON.stringify(data).replace(/</g, '\\u003c');
